import { Fragment } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { safeParseDecimal } from "@/lib/excel";
import Decimal from "decimal.js";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { SummaryInfo } from "@/lib/api/directSales";

// Helper function to format numbers
const formatNumber = (value: Decimal | string | number | undefined | null, fractionDigits = 2): string => {
  const decimalValue = safeParseDecimal(value || 0);
  if (decimalValue.isNaN()) {
    return "N/A";
  }
  return decimalValue.toFixed(fractionDigits).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

// Helper function to format currency
const formatCurrency = (value: Decimal | string | number | undefined | null): string => {
  const decimalValue = safeParseDecimal(value || 0);
  if (decimalValue.isNaN()) {
    return "N/A";
  }
  const formatted = decimalValue.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return `$${formatted}`;
};

// Type for the processed hierarchical data
type StatusSummaryData = {
  total_qty: Decimal;
  total_marketing_fee: Decimal;
  years: Record<number, {
    total_qty: Decimal;
    total_marketing_fee: Decimal;
    months: Record<string, {
      month_display: string;
      total_qty: Decimal;
      total_marketing_fee: Decimal;
    }>;
    sortedMonths: string[];
  }>;
  sortedYears: number[];
};

type StatusBreakdown = Record<"open" | "closed", StatusSummaryData>;

function buildStatusBreakdown(summary: SummaryInfo): StatusBreakdown {
  const result: StatusBreakdown = {
    open: { total_qty: new Decimal(0), total_marketing_fee: new Decimal(0), years: {}, sortedYears: [] },
    closed: { total_qty: new Decimal(0), total_marketing_fee: new Decimal(0), years: {}, sortedYears: [] },
  };

  // Process Year Summaries
  summary.yearSummary.forEach(item => {
    const status = item.status;
    const year = item.year;
    const qty = safeParseDecimal(item.total_qty);
    const fee = safeParseDecimal(item.total_marketing_fee);

    result[status].total_qty = result[status].total_qty.plus(qty);
    result[status].total_marketing_fee = result[status].total_marketing_fee.plus(fee);

    if (!result[status].years[year]) {
      result[status].years[year] = { total_qty: new Decimal(0), total_marketing_fee: new Decimal(0), months: {}, sortedMonths: [] };
    }
    result[status].years[year].total_qty = result[status].years[year].total_qty.plus(qty);
    result[status].years[year].total_marketing_fee = result[status].years[year].total_marketing_fee.plus(fee);
  });

  // Process Month Summaries
  summary.monthSummary.forEach(item => {
    const status = item.status;
    const year = item.year;
    const month = item.month;
    const month_display = item.month_display;
    const qty = safeParseDecimal(item.total_qty);
    const fee = safeParseDecimal(item.total_marketing_fee);
    const monthKey = `${year}-${month.toString().padStart(2, '0')}`;

    if (!result[status].years[year]) {
      result[status].years[year] = { total_qty: new Decimal(0), total_marketing_fee: new Decimal(0), months: {}, sortedMonths: [] };
    }

    if (!result[status].years[year].months[monthKey]) {
      result[status].years[year].months[monthKey] = { month_display: month_display, total_qty: new Decimal(0), total_marketing_fee: new Decimal(0) };
    }

    result[status].years[year].months[monthKey].total_qty = result[status].years[year].months[monthKey].total_qty.plus(qty);
    result[status].years[year].months[monthKey].total_marketing_fee = result[status].years[year].months[monthKey].total_marketing_fee.plus(fee);
  });

  // Sort years and months
  (['open', 'closed'] as const).forEach(status => {
    result[status].sortedYears = Object.keys(result[status].years).map(Number).sort((a, b) => a - b);
    result[status].sortedYears.forEach(year => {
      result[status].years[year].sortedMonths = Object.keys(result[status].years[year].months).sort((a, b) => a.localeCompare(b));
    });
  });

  return result;
}

export function SummaryTable({ summary }: { summary: SummaryInfo }) {
  const statusBreakdown = buildStatusBreakdown(summary);

  const grandTotalQty = statusBreakdown.open.total_qty.plus(statusBreakdown.closed.total_qty);
  const grandTotalFee = statusBreakdown.open.total_marketing_fee.plus(statusBreakdown.closed.total_marketing_fee);

  return (
    <section className="grid gap-4 grid-cols-1 md:grid-cols-2">
      {/* Card 1: Status Breakdown */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Totals by Status</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {(['open', 'closed'] as const).map(status => {
            const statusData = statusBreakdown[status];
            if (statusData.total_qty.eq(0)) return null;
            return (
              <div key={status} className="p-2 bg-muted rounded-md">
                <h4 className="font-medium mb-1 uppercase text-xs">{status}</h4>
                <div className="flex justify-between items-center text-xs">
                  <span className="text-gray-600">Total Bushel Quantity:</span>
                  <span className="tabular-nums">{formatNumber(statusData.total_qty)}</span>
                </div>
                <div className="flex justify-between items-center text-xs">
                  <span className="text-gray-600">Total Marketing Fee:</span>
                  <span className="tabular-nums">{formatCurrency(statusData.total_marketing_fee)}</span>
                </div>
              </div>
            );
          })}
          {grandTotalQty.eq(0) && (
            <p className="text-sm text-muted-foreground text-center">No data available.</p>
          )}
        </CardContent>
      </Card>

      {/* Card 2: Status -> Year Breakdown */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Totals by Status and Year</CardTitle>
        </CardHeader>
        <CardContent>
          <Table className="compact-table">
            <TableHeader>
              <TableRow className="h-8">
                <TableHead className="py-1">Status / Year</TableHead>
                <TableHead className="py-1 text-right">Total Bushel Qty</TableHead>
                <TableHead className="py-1 text-right">Total Marketing Fee</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(['open', 'closed'] as const).map(status => {
                const statusData = statusBreakdown[status];
                if (statusData.total_qty.eq(0)) return null;

                return (
                  <Fragment key={`status-year-${status}`}>
                    {/* Status Header Row */}
                    <TableRow className="h-7 text-sm bg-gray-200 dark:bg-gray-700">
                      <TableCell className="py-1 font-semibold uppercase">{status}</TableCell>
                      <TableCell className="py-1 text-right tabular-nums font-semibold">{formatNumber(statusData.total_qty)}</TableCell>
                      <TableCell className="py-1 text-right tabular-nums font-semibold">{formatCurrency(statusData.total_marketing_fee)}</TableCell>
                    </TableRow>

                    {/* Year Rows */}
                    {statusData.sortedYears.map(year => {
                      const yearData = statusData.years[year];
                      if (yearData.total_qty.eq(0)) return null;

                      return (
                        <TableRow key={`${status}-year-${year}`} className="h-6 text-xs">
                          <TableCell className="py-0.5 pl-8">{year}</TableCell>
                          <TableCell className="py-0.5 text-right tabular-nums">{formatNumber(yearData.total_qty)}</TableCell>
                          <TableCell className="py-0.5 text-right tabular-nums">{formatCurrency(yearData.total_marketing_fee)}</TableCell>
                        </TableRow>
                      );
                    })}
                  </Fragment>
                );
              })}
              {grandTotalQty.eq(0) && (
                <TableRow>
                  <TableCell colSpan={3} className="h-24 text-center">
                    No data available.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>


      {/* Card 3: Status -> Year -> Month Breakdown */}
      <Card className="col-span-full">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Totals by Status, Year, and Month</CardTitle>
        </CardHeader>
        <CardContent>
          <Table className="compact-table">
            <TableHeader>
              <TableRow className="h-8">
                <TableHead className="py-1">Status / Year / Month</TableHead>
                <TableHead className="py-1 text-right">Total Bushel Qty</TableHead>
                <TableHead className="py-1 text-right">Total Marketing Fee</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(['open', 'closed'] as const).map(status => {
                const statusData = statusBreakdown[status];
                if (statusData.total_qty.eq(0)) return null;

                return (
                  <Fragment key={`status-month-${status}`}>
                    {/* Status Header Row */}
                    <TableRow className="h-7 text-sm bg-gray-200 dark:bg-gray-700">
                      <TableCell className="py-1 font-semibold uppercase">{status}</TableCell>
                      <TableCell className="py-1 text-right tabular-nums font-semibold">{formatNumber(statusData.total_qty)}</TableCell>
                      <TableCell className="py-1 text-right tabular-nums font-semibold">{formatCurrency(statusData.total_marketing_fee)}</TableCell>
                    </TableRow>

                    {/* Year Rows */}
                    {statusData.sortedYears.map(year => {
                      const yearData = statusData.years[year];
                      if (yearData.total_qty.eq(0)) return null;

                      return (
                        <Fragment key={`${status}-year-month-${year}`}>
                          <TableRow className="h-6 text-xs bg-muted/50">
                            <TableCell className="py-0.5 pl-8 font-bold">{year}</TableCell>
                            <TableCell className="py-0.5 text-right tabular-nums font-medium">{formatNumber(yearData.total_qty)}</TableCell>
                            <TableCell className="py-0.5 text-right tabular-nums font-medium">{formatCurrency(yearData.total_marketing_fee)}</TableCell>
                          </TableRow>

                          {/* Month Rows */}
                          {yearData.sortedMonths.map(monthKey => {
                            const monthData = yearData.months[monthKey];
                            if (monthData.total_qty.eq(0)) return null;

                            return (
                              <TableRow key={`${status}-month-${monthKey}`} className="h-6 text-xs">
                                <TableCell className="py-0.5 pl-14">{monthData.month_display}</TableCell>
                                <TableCell className="py-0.5 text-right tabular-nums">{formatNumber(monthData.total_qty)}</TableCell>
                                <TableCell className="py-0.5 text-right tabular-nums">{formatCurrency(monthData.total_marketing_fee)}</TableCell>
                              </TableRow>
                            );
                          })}
                        </Fragment>
                      );
                    })}
                  </Fragment>
                );
              })}
              {grandTotalQty.eq(0) && (
                <TableRow>
                  <TableCell colSpan={3} className="h-24 text-center">
                    No data available.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </section>
  );
}
